<template>
    <div ref="container" class="container-body" @wheel="handleWheel">
        <div v-if="load" class="load">
            <ui-load-indicator
                bg-color="#DFE5EC"
                color="#69D296"
                :h="70"
                :stroke="8"
            />
        </div>
        <div ref="viewport" class="viewport" @mousedown="startDrag">
            <div ref="image" class="pannable-image" :style="imageStyle">
                <div
                    v-for="(ball, index) in unitBalls"
                    :key="index"
                    :style="{ left: ball.coord_x + 'px', top: ball.coord_y + 'px' }"
                    class="ball"
                >
                    <ui-ball
                        :data="ball"
                        :is-click="true"
                        :size-ball="sizeBall"
                        :set-color="situation[ball.situation]"
                        @input="detailsVisualization"
						:isBallPulse="ball.showPulse"
                    />
                </div>
            </div>
        </div>
        <div v-if="units.length" class="input-zoom">
            <div class="content">
                <input v-model="zoomLevel" type="range" min="0.5" max="1" step="0.1" />
            </div>
            <div class="content">
                <button @click="toggleFullScreen">
                    <i :class="fullScreen ? 'fas fa-compress' : 'fas fa-expand'" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
	import { uiBall, uiLoadIndicator } from '@/components/ui'

	import { availability, product } from '@/services/axios'

	import { storage } from '@/services/storage'

	import { mapActions, mapGetters } from 'vuex'

	import panzoom from 'panzoom'

	import { alertMixin, utilMixin } from '@/mixins'

	export default {
		name: 'ui-image-zoom',

		components: {
			uiBall,
			uiLoadIndicator
		},

		mixins: [alertMixin, utilMixin],

		data() {
			return {
				id: null,
				zoomLevel: 0.5,
				viewport: null,
				pannableImage: null,
				iconPosition: null,
				load: false,
				units: [],
				sizeBall: 0,
				situation: null,
				backgroundImage: '',
				imageUrl: '',
				imageStyle: {
					position: 'absolute',
					overflow: 'hidden',
					transform: 'scale(0.5)',
					cursor: 'grab'
				},
				last_page: 2,
				total: 0,
				panzoomInstance: null,
				destroyPanzoom: false,
				fullScreen: false,
				fullscreenEventListener: null,
				syncValue: null,
				isBallPulse: false,
				allowedApps: storage.get('allowed_apps') || [],
				imageStyleStorage: JSON.parse(storage.get('zoom_position_image')),
				uiOriginalToastParent: null
			}
		},

		computed: {
			/**
			 * Computed property que retorna as unidades que foram mapeadas para a imagem de
			 * implantação
			 */
			unitBalls() {
				return this.units.filter(unit => unit.coord_x && unit.coord_y)
			},

			// Websocket
			...mapGetters('websocketDeploymentImageModule', ['getControl', 'getUnityEvent', 'getPusher', 'getChannel']),
		},

		watch: {
			zoomLevel(value) {
				if (!value) {
					value = this.getValueInsideParentheses(this.imageStyle.transform)
				}
				this.imageStyle.transform = `scale(${value ? value : this.getValueInsideParentheses(this.imageStyle.transform)})`
				this.zoomLevel = value
				this.imageStyleStorage = this.imageStyle
				storage.set('zoom_position_image', JSON.stringify(this.imageStyle))
			},
			getUnityEvent: {
				handler(unityEvent) {
					if (unityEvent && unityEvent.id) {
						// chama as funções que atualiza os status a bolinha e o toast
						this.processUnityEvent(unityEvent);
						this.onChangeColorEvent(unityEvent)
					}
				},
				immediate: true,
				deep: true
  			}
		},

		async mounted() {
			this.id = this.$route.params.id
			this.setupPanzoom()
			this.observeVisibility()
			await this.returnData()

			// inicializa o websocket caso tenha o websocket no allowedApps
			if(this.allowedApps.includes("websocket")){
				// Inicializa o Pusher
				this.initializePusher()

				// Vincula o evento ao canal
				this.bindChannelEvent({
					eventName: `product.${this.$route.params.id}`
				})
			}

			this.uiOriginalToastParent = document.querySelector('.toast-bottom-right').parentNode

			if(this.imageStyleStorage) {
				this.imageStyle.transform = this.imageStyleStorage.transform

				this.zoomLevel = Number(this.getValueInsideParentheses(this.imageStyle.transform))
			}

			this.loadPosition()
		},

		beforeDestroy() {
			// chama o metodo que desconecta o websocker ao componente ser destruído
			this.disconnectPusher()
		},

		methods: {
			...mapActions('product', ['setProduct', 'set']),
			// Pega do store a inicialização do websocket
			...mapActions('websocketDeploymentImageModule', ['initializePusher', 'bindChannelEvent',  'disconnectPusher', 'clearUnityEvent' ]),
			...mapActions('detailsHasPermissions', ['updateDetails']),

			observeVisibility() {
				const observer = new IntersectionObserver((entries) => {
					entries.forEach(entry => {
						if (!entry.isIntersecting) {
							this.panzoomInstance.moveTo(0, 0)
							this.panzoomInstance.zoomTo(0, 0, this.zoomLevel)
						}
					})
				}, { threshold: 0 })

				// Observar o elemento da imagem
				if (this.$refs.image) {
					observer.observe(this.$refs.image)
				}
			},

			startDrag() {
				document.addEventListener("mouseup", this.stopDrag)
			},

			stopDrag() {
				document.removeEventListener("mouseup", this.stopDrag)
				this.savePosition() // Salva a posição no localStorage
			},

			savePosition() {
				let transformPanzoom = this.panzoomInstance.getTransform()

				let savedPositions = JSON.parse(storage.get("imagePositions")) || []

				const newPosition = {
					id: Number(this.id),
					x: transformPanzoom.x,
					y: transformPanzoom.y
				}

				// Verifica se já existe um objeto com o mesmo ID
				const index = savedPositions.findIndex(item => item.id === newPosition.id)

				if (index !== -1) {
					savedPositions[index] = newPosition
				} else {
					savedPositions.push(newPosition)
				}

				storage.set("imagePositions", JSON.stringify(savedPositions))
			},

			loadPosition() {
				let savedPositions = JSON.parse(storage.get("imagePositions")) || []

				let foundObject = savedPositions.find(item => item.id === Number(this.id))

				if (foundObject) {
					const { x, y } = foundObject

					if (this.panzoomInstance) {
						// Aplica o estado salvo
						this.panzoomInstance.moveTo(x, y);
						this.panzoomInstance.zoomTo(x, y, this.zoomLevel);
					}
				}
			},

			getColor(hexcolor){
				if(!hexcolor.startsWith('#') ) return 'white'
				hexcolor = hexcolor.replace("#", "");

				let r = parseInt(hexcolor.substring(0,2), 16);
				let g = parseInt(hexcolor.substring(2,4), 16);
				let b = parseInt(hexcolor.substring(4,6), 16);

				let yiq = ((r*299) + (g*587) + (b*114)) / 1000;

				return (yiq >= 128) ? 'black' : 'white';
			},

			/**
			 *
			 * Método para desconectar o Pusher
			*/
			disconnectPusher() {
				const pusherInstance = this.getPusher;
				const channel = this.getChannel;

				if (channel) {
					channel.unbind_all();
					channel.unsubscribe();
				}

				if (pusherInstance) {
					pusherInstance.disconnect();
				}

				// Reseta estado Vuex manualmente
				this.$store.commit('websocketDeploymentImageModule/DISCONNECT_PUSHER');
			},

			/**
			 *
			 * @param unityEvent {Object} unityEvent
			 * Dispara o toast informando o status atualizado da unidade
			 */
			processUnityEvent(unityEvent) {
				if (unityEvent.id) {
					let message;
					if (this.availabilityType === 'vertical') {
						message = `(Torre: ${unityEvent.block} - Unidade: ${unityEvent.number})`;
					} else {
						message = `(Quadra: ${unityEvent.block} - Lote: ${unityEvent.number})`;
					}
					const text = `A unidade ${message} foi definida como: ${unityEvent.status.name}`;
					const fontColor = this.getColor(unityEvent.status.color)
					this.$toastr.Add({
						name: "websocketunity",
						title: " ",
						msg: text,
						position: "toast-bottom-right",
						style: {
							backgroundColor: unityEvent.status.color,
							color: fontColor,
							fontWeight: 600,
							letterSpacing: "0.05em",
						}
					})
				}
			},
			/**
			 *
			 * @param unityEvent {Object} unityEvent
			 * Muda a calor e adiciona a animação de pulsar ao atualizar o status da unidade
			 */
			onChangeColorEvent(unityEvent) {
				const updateSummary = [...this.units]

				this.$store.dispatch('product/updateSummary', {unityEvent, units: updateSummary});

				const updateUnits = this.units.map(unit => {
					if(unit.id === unityEvent.id) {
						return {
							...unit,
							situation: unityEvent.situation,
							showPulse: true
						}
					}
					return unit
				})

				this.units = updateUnits


				setTimeout(() => {
					this.units = this.units.map(unit => {
						if(unit.id === unityEvent.id){
							return {
								...unit,
								showPulse: false
							}
						}
						return unit
					})
				}, 15000)
				this.clearUnityEvent()
			},
			/**
			 * Abre o modal para mostrar os detalhes da unidade
			 */
			detailsVisualization(ball) {
                let payload = {
					id: ball.integration_id || ball.id,
					visualization: 'implantationimage'
				}
				if (ball.integration_id) payload.availability_id = this.product?.availability_id
				this.$root.$emit('modal-open-units-data', payload)
			},
			setupPanzoom() {
				const container = this.$refs.viewport

				this.panzoomInstance = panzoom(container, {
					maxZoom: 1,
        			minZoom: 0.5,
					disableZoom: true,
					zoomDoubleClickSpeed: 0,
					smoothScroll: false,
				})
			},
			async returnData() {
				this.load = true
				this.syncValue = this.$route.query.sync

				let params = {
					header: true,
					visualization: 'implantationimage',
					filter: 'page:1;per_page:800',
					sync: this.syncValue
				}

				try {
					const response = await product.getV2(`${this.$route.params.id}/units`, '', params)
					const responseData = response?.data?.data
					// Para empreendimentos integração V1, solicita o sincronismo das unidades
					if (responseData?.availability?.integration_erp_id &&
						responseData?.availability?.integration_erp?.version !== 'v2') {
						availability.getBlocks(responseData.availability_id)
					}

					this.setProduct(responseData)

					//carrega todas unidades
					this.units = responseData?.availability?.units ? [...responseData.availability.units] : []

					//carrega a imagem de implantação
					this.backgroundImage = responseData?.availability?.file_map?.url ? responseData.availability.file_map.url : ''

					const getSizeBall = () => {
						return responseData?.infos?.cSizeBall ?? responseData?.infos?.sizeBall ?? 35
					}

					//carrega o tamanho da bolinha
					this.sizeBall = getSizeBall()

					//carrega as cores das bolas
					this.situation = responseData?.situations ? responseData.situations : {}

					// carrega image da visualização
					if (this.backgroundImage) await this.mountImage()

					/**
					 * As informações já então carregadas então deve desativa o header para carregar o
					 * restante das unidades com mais velocidade e evitar demora na página.
					 * */
					params.header = false

					// Adiciona ao array o restante das unidades
					for (const index of [...Array(responseData.pagination.last_page - 1).keys()].map(key => key + 2)) {
						params.filter = `page:${index};per_page:800`

						const { data } = await product.getV2(`${this.$route.params.id}/units`, '', params)
						this.units = this.units.concat(data.data.availability.units)
					}
					this.load = false
				} catch (error) {
					this.$mx_alertError('Erro ao obter dados:', error)

					this.load = false
					if (!error?.data?.msg) {
						this.updateDetails(error?.data?.msg)
						this.$router.push({ name: 'products.dataUnits' })
					}

					if (!error?.data?.details?.hasPermission) {
						this.updateDetails(error?.data?.details)
						this.$router.push({ name: 'products.dataUnits' })
					}
				}
			},
			mountImage() {
				this.pannableImage = this.$refs.image

				// Adicione a propriedade will-change para otimização de renderização
				this.pannableImage.style.willChange = 'transform'

				// Criar um novo objeto de imagem para obter as dimensões reais da imagem de fundo
				const img = new Image()
				img.src = this.backgroundImage
				img.classList.add('image-deployment-charged')
				img.onload = () => {
					this.pannableImage.appendChild(img)
				}
				// Adiciona a imagem se existir dentro da div pannableImage
			},
			handleWheel(e) {
				e.preventDefault()
				if (e.deltaY > 0) {
					// Scroll para baixo, diminuir o zoom
					this.zoomLevel = Math.max(0.5, this.zoomLevel - 0.1)

				} else {
					// Scroll para cima, aumentar o zoom
					this.zoomLevel = Math.min(1, this.zoomLevel + 0.1)
				}
			},
			toggleFullScreen() {
				if (!this.fullScreen) {
					// Entrar no modo de tela cheia
					if (this.$refs.container.requestFullscreen) {
						this.fullscreenEventListener = () => {
							this.enterFullScreen()
							document.removeEventListener('click', this.fullscreenEventListener)
						}

						document.addEventListener('click', this.fullscreenEventListener)
					} else {
						this.$mx_alertError('O modo de tela cheia não é suportado neste navegador.')
					}
				} else {
					// Sair do modo de tela cheia
					if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) {
						if (document.exitFullscreen) {
							document.exitFullscreen()
							.then(() => this.uiToggleToastLocation(false))
							.catch((err) => {
								this.$mx_alertError('Erro ao tentar sair do modo de tela cheia:', err)
							})
						}
					}
					// Limpar o ouvinte de evento quando sair do modo de tela cheia
					if (this.fullscreenEventListener) {
						document.removeEventListener('click', this.fullscreenEventListener)
						this.fullscreenEventListener = null
					}
				}

      			this.fullScreen = !this.fullScreen
    		},
			enterFullScreen() {
				this.$refs.container.requestFullscreen()
				.then(() => this.uiToggleToastLocation(true))
				.catch((err) => {
					this.$mx_alertError('Erro ao tentar ativar o modo de tela cheia:', err)
				})
			},
			/**
			 * Fazer o toast aparecer com o modo fullscreen ativado
			 */
			uiToggleToastLocation(isFullscreen) {
    		    const toastToBeRelocated = document.querySelector('.toast-bottom-right');
    		    if (!toastToBeRelocated) return

				const fullscreenElement = this.$refs.container

    		    if (isFullscreen && fullscreenElement) {
					fullscreenElement.appendChild(toastToBeRelocated);
    		    } else if(this.uiOriginalToastParent){
    		        this.uiOriginalToastParent.appendChild(toastToBeRelocated);
    		    }
    		},

			getValueInsideParentheses(input) {
				const match = input.match(/\((.*?)\)/) // Procura por valores dentro de parênteses
				return match ? match[1] : null // Retorna o valor encontrado ou null se não houver
			}
		}
	}
</script>

	<style lang="stylus" scoped>
		@import './styles.styl'
	</style>
